/**
 * Soft wires (ART_BIBLE §7): a chain of points joined by rods, pulled down by gravity and nudged
 * by a breath of wind (Verlet integration). Pure numbers: the picture reads the points and draws
 * a curve through them. A wire has both ends pinned to its terminals.
 */

export interface Node {
  x: number;
  y: number;
  /** Where it was one step ago: the speed is the difference. */
  px: number;
  py: number;
}

export interface Wire {
  nodes: Node[];
  /** The length of each rod. Longer than the gap, so the wire sags. */
  rod: number;
  /** Where the two ends are pinned. */
  from: { x: number; y: number };
  to: { x: number; y: number };
}

/** A wire hanging between two terminals, `slack` (≥ 1) times as long as the straight line. */
export function createWire(
  from: { x: number; y: number },
  to: { x: number; y: number },
  count = 9,
  slack = 1.18,
): Wire {
  const nodes: Node[] = [];
  for (let i = 0; i < count; i++) {
    const t = i / (count - 1);
    const x = from.x + (to.x - from.x) * t;
    const y = from.y + (to.y - from.y) * t;
    nodes.push({ x, y, px: x, py: y });
  }
  const gap = Math.hypot(to.x - from.x, to.y - from.y);
  return { nodes, rod: (gap * slack) / (count - 1), from: { ...from }, to: { ...to } };
}

export interface Forces {
  gravity?: number;
  /** Sideways push in px/s², already varying in time. */
  wind?: number;
  /** Fraction of speed kept each step. */
  damping?: number;
}

/** One step of `dt` seconds. The two ends stay where they are pinned. */
export function stepWire(w: Wire, dt: number, f: Forces = {}): void {
  const g = f.gravity ?? 520;
  const wind = f.wind ?? 0;
  const keep = f.damping ?? 0.985;
  const h = Math.min(0.05, Math.max(0, dt));
  const last = w.nodes.length - 1;
  for (let i = 1; i < last; i++) {
    const n = w.nodes[i] as Node;
    const vx = (n.x - n.px) * keep;
    const vy = (n.y - n.py) * keep;
    n.px = n.x;
    n.py = n.y;
    n.x += vx + wind * h * h;
    n.y += vy + g * h * h;
  }
  const a = w.nodes[0] as Node;
  const b = w.nodes[last] as Node;
  a.x = a.px = w.from.x;
  a.y = a.py = w.from.y;
  b.x = b.px = w.to.x;
  b.y = b.py = w.to.y;
  // A few rounds of pulling every rod back to its length.
  for (let round = 0; round < 6; round++) {
    for (let i = 0; i < last; i++) {
      const p = w.nodes[i] as Node;
      const q = w.nodes[i + 1] as Node;
      const dx = q.x - p.x;
      const dy = q.y - p.y;
      const d = Math.hypot(dx, dy) || 1e-6;
      const diff = (d - w.rod) / d;
      const pinnedP = i === 0;
      const pinnedQ = i + 1 === last;
      const share = pinnedP || pinnedQ ? 1 : 0.5;
      if (!pinnedP) {
        p.x += dx * diff * share;
        p.y += dy * diff * share;
      }
      if (!pinnedQ) {
        q.x -= dx * diff * share;
        q.y -= dy * diff * share;
      }
    }
  }
}

/** Runs the wire until it hangs still, so that a static picture has the right sag. */
export function settleWire(w: Wire, seconds = 1.5): void {
  for (let t = 0; t < seconds; t += 1 / 60) stepWire(w, 1 / 60, { damping: 0.9 });
}

/** The path of a smooth curve through the points (quadratic through the midpoints). */
export function wirePath(w: Wire): string {
  const n = w.nodes;
  const first = n[0];
  if (!first) return '';
  let d = `M${first.x.toFixed(1)} ${first.y.toFixed(1)}`;
  for (let i = 1; i < n.length - 1; i++) {
    const p = n[i] as Node;
    const q = n[i + 1] as Node;
    d += ` Q${p.x.toFixed(1)} ${p.y.toFixed(1)} ${((p.x + q.x) / 2).toFixed(1)} ${((p.y + q.y) / 2).toFixed(1)}`;
  }
  const end = n[n.length - 1] as Node;
  return `${d} L${end.x.toFixed(1)} ${end.y.toFixed(1)}`;
}

/** The longest distance of any point from where a straight wire would be: how much it sags. */
export function sag(w: Wire): number {
  const dx = w.to.x - w.from.x;
  const dy = w.to.y - w.from.y;
  const len = Math.hypot(dx, dy) || 1;
  let worst = 0;
  for (const p of w.nodes) {
    const off = Math.abs((p.x - w.from.x) * dy - (p.y - w.from.y) * dx) / len;
    worst = Math.max(worst, off);
  }
  return worst;
}
